import { useEffect, useId, useRef, useState } from 'react';
import { mapkitReady } from '../mapkit-loader.ts';
import { Label } from '../ui.tsx';
import { FIELD, FIELD_LABEL } from './form.tsx';
import { askMyLocation, canAskLocation } from './my-location.ts';
import { regionNear, type SearchRegion } from './search-region.ts';

/**
 * Address input with MapKit JS autocomplete underneath.
 *
 * Uses `mapkit.Search.autocomplete()` — the same MapKit JS instance the
 * dashboard's maps ride on, so no new vendor, no CSP change, no per-query
 * cost. Google Places was never an option (`docs/growth-triggers.md` and
 * `north-star.md`: no third-party places API on the alerting-adjacent
 * surface, and $275/mo per env we do not spend).
 *
 * Falls back to a plain text field when MapKit is unavailable — the same
 * degrade path the map engine takes (`mapkit-loader.ts`). An address you
 * had to type by hand is a smaller regression than a screen that refuses
 * to render because the map engine is down.
 *
 * Emits both the human-readable string (what goes on file, what the org
 * knows their building as) and, when a suggestion is chosen, the
 * coordinate MapKit gave back — so a downstream site-detail map can
 * render the pin without geocoding again.
 */
export function AddressAutocomplete({
  value,
  onChange,
  onPick,
  placeholder,
  label,
  eager = false,
  preview = true,
  region: regionProp,
  offerMyLocation = false,
  appearance = 'default',
}: {
  value: string;
  /** Called on every keystroke. Also called with the picked suggestion's
   *  full address when a row is chosen. */
  onChange: (address: string) => void;
  /** Called only when a suggestion is picked, with the coordinate MapKit
   *  returned. Useful for the site-detail map. Optional — a plain-text
   *  address still saves without one. */
  onPick?: (coord: { latitude: number; longitude: number } | null) => void;
  placeholder?: string;
  label?: string;
  /**
   * Start loading MapKit's script on MOUNT rather than waiting for the field to
   * be focused. Off by default (a People/detail screen that merely renders the
   * field should not pull ~200KB), on where the caller knows the address will be
   * used — the building wizard — so results are ready the instant the user types
   * instead of waiting on a cold download (Trav 2026-09-19: "takes ages").
   */
  eager?: boolean;
  /**
   * Show the inline preview map under the field when a coordinate is picked. On
   * by default; the building wizard turns it OFF because a 180px map appearing
   * mid-form shoved every field below it around (Trav 2026-09-19). The building
   * lands on the persistent map after creation regardless.
   */
  preview?: boolean;
  /**
   * Where to look first (`lib/search-region.ts`) — a BIAS, never a fence: an
   * address outside it still comes back, it just ranks below look-alikes
   * inside it. Omitted, MapKit searches with no hint (the old behaviour).
   */
  region?: SearchRegion | null;
  /**
   * Offer "Use my location" beside the label. The browser is asked ONLY when
   * it is tapped; the answer biases this search for the session and goes
   * nowhere else (`lib/my-location.ts`). A refusal keeps the given region.
   */
  offerMyLocation?: boolean;
  /**
   * `field`: the dashboard's form register (`lib/form.tsx`) — the 44px field
   * with the label floating inside it, for a form where every other control
   * wears it (the building wizard). The default keeps the uppercase label
   * above a 36px input, which the building page and the tests expect.
   */
  appearance?: 'default' | 'field';
}) {
  const field = appearance === 'field';
  // The label names the input (it did not — a screen reader heard a bare
  // text box, and so did the tests).
  const inputId = useId();
  // What the person chose to share wins over the computed hint — they asked.
  const [mine, setMine] = useState<SearchRegion | null>(null);
  const [asking, setAsking] = useState(false);
  const region = mine ?? regionProp ?? undefined;
  const useMyLocation = () => {
    setAsking(true);
    void askMyLocation().then((near) => {
      setAsking(false);
      if (near) setMine(regionNear(near));
    });
  };
  const [suggestions, setSuggestions] = useState<mapkit.AutocompleteResult[]>([]);
  const [open, setOpen] = useState(false);
  /**
   * The picked suggestion carried no coordinate (a POI row MapKit could not
   * resolve). Saying so beats the old silence — the caller's `if (!c) return`
   * left the person with a filled field, no pin, and no idea why.
   */
  const [pickedWithoutCoord, setPickedWithoutCoord] = useState(false);
  const [mapkitOn, setMapkitOn] = useState(false);
  // The coordinate of the currently-picked suggestion, held here so the
  // preview map underneath the field can render a pin. Distinct from what
  // the parent's `onPick` receives — parents can consume the coordinate for
  // their own reasons and the preview always renders regardless.
  const [previewCoord, setPreviewCoord] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);
  const search = useRef<mapkit.Search | null>(null);
  // MapKit's `Search.autocomplete()` returns a numeric REQUEST ID, not a
  // cancellable object — you cancel by handing the id back to the same Search
  // instance via `search.cancel(id)`. Storing the return and calling
  // `.cancel()` on it threw "cancel is not a function" on the second keystroke
  // (and on the debounce cleanup), crashing the add-building form.
  const inFlight = useRef<number | null>(null);
  // Every request is numbered; only the LATEST one's answer is ever shown.
  // The root cause of "after the first address it stops finding places"
  // (Trav, 2026-09-30): an older request's answer — the autocomplete fired
  // for the just-picked address, or a keystroke's query MapKit had already
  // answered when we cancelled it — could land AFTER the newer one and
  // replace its suggestions with the old address's rows, and its callback
  // nulled `inFlight` so the newer request could no longer be cancelled.
  // Typing "380 St Kilda Rd" then showed "420 St Kilda Rd" results, or none.
  const seq = useRef(0);
  // The string a pick wrote into the field. Searching it again is a wasted
  // quota unit whose (late) answer used to reopen the old list; it is skipped
  // until the person types something else.
  const picked = useRef<string | null>(null);
  const resolveSeq = useRef(0);
  // A real failure of the latest request is said, not shown as "no results".
  const [searchFailed, setSearchFailed] = useState(false);
  const regionKey = region
    ? `${region.latitude},${region.longitude},${region.latitudeDelta},${region.longitudeDelta}`
    : '';
  const mapEl = useRef<HTMLDivElement | null>(null);
  const map = useRef<mapkit.Map | null>(null);
  const marker = useRef<mapkit.MarkerAnnotation | null>(null);

  // Lazy: only probe when the field is focused, since MapKit's script is
  // ~200KB and most sessions never touch the site-add form. `mapkitReady`
  // is memoised so a probe elsewhere in the app costs nothing here.
  useEffect(() => {
    if ((!open && !eager) || mapkitOn) return;
    void mapkitReady().then((ready) => {
      if (!ready) return;
      setMapkitOn(true);
    });
  }, [open, eager, mapkitOn]);

  // One Search per region: the bias is the constructor's, so a new region (the
  // org's buildings arriving) is a new instance. Never `getsUserLocation` — no
  // permission prompt in front of an address field.
  useEffect(() => {
    if (!mapkitOn) return;
    const mk = window.mapkit!;
    const priority = mk.Search.RegionPriority?.Default;
    search.current = new mk.Search(
      region
        ? {
            region: toMapkitRegion(region),
            ...(priority !== undefined ? { regionPriority: priority } : {}),
            getsUserLocation: false,
          }
        : { getsUserLocation: false },
    );
    inFlight.current = null;
    // `region` is keyed by value (regionKey), not identity.
  }, [mapkitOn, regionKey]);

  // Debounce autocomplete calls: 200ms is enough for a normal typing
  // cadence and short enough that a paused typer sees suggestions
  // within a beat. MapKit itself is fast, but every autocomplete call
  // burns a MapKit quota unit, and one per keystroke is wasteful.
  useEffect(() => {
    // Any change supersedes whatever was asked before — its answer, if it
    // still lands, is ignored by number.
    seq.current += 1;
    if (!mapkitOn || !search.current || !value || value.length < 3) {
      setSuggestions([]);
      return;
    }
    // The field holds exactly what was just picked: nothing to look up.
    if (picked.current !== null && value === picked.current) return;
    const mine = seq.current;
    const handle = setTimeout(() => {
      const s = search.current;
      if (!s) return;
      if (inFlight.current !== null) s.cancel(inFlight.current);
      const id = s.autocomplete(
        value,
        (err, data) => {
          // A superseded (or cancelled) request's answer never touches the list.
          if (mine !== seq.current) return;
          inFlight.current = null;
          if (err) {
            setSuggestions([]);
            setSearchFailed(true);
            return;
          }
          setSearchFailed(false);
          setSuggestions(data.results.slice(0, 6));
        },
        region ? { region: toMapkitRegion(region) } : undefined,
      );
      // MapKit may answer synchronously; only a still-current request is
      // worth remembering for cancellation.
      if (mine === seq.current) inFlight.current = id;
    }, 200);
    return () => {
      clearTimeout(handle);
      if (inFlight.current !== null) {
        search.current?.cancel(inFlight.current);
        inFlight.current = null;
      }
    };
  }, [value, mapkitOn, regionKey]);

  const pick = (r: mapkit.AutocompleteResult) => {
    // Prefer the concatenation of both display lines when both exist —
    // Apple splits "Lincoln Primary School" and its address across lines
    // 0 and 1; joining is what a user reads it as. Trim guards against
    // an empty second line.
    const full = [r.displayLines[0], r.displayLines[1]].filter(Boolean).join(', ');
    picked.current = full;
    onChange(full);
    setSuggestions([]);
    setOpen(false);
    setSearchFailed(false);
    const land = (coord: { latitude: number; longitude: number } | null) => {
      setPreviewCoord(coord);
      setPickedWithoutCoord(coord === null);
      onPick?.(coord);
    };
    if (r.coordinate) {
      land({ latitude: r.coordinate.latitude, longitude: r.coordinate.longitude });
      return;
    }
    // An address row often comes back without a coordinate; a full search on
    // the row itself resolves it (MapKit's own documented path) before we say
    // there is no location.
    const s = search.current;
    if (!s || typeof s.search !== 'function') {
      land(null);
      return;
    }
    // Numbered on its own counter: the value change this pick just made bumps
    // `seq`, and must not orphan the resolution it started.
    const mine = ++resolveSeq.current;
    s.search(r, (err, data) => {
      if (mine !== resolveSeq.current || picked.current !== full) return;
      const c = err ? undefined : data.places?.[0]?.coordinate;
      land(c ? { latitude: c.latitude, longitude: c.longitude } : null);
    });
  };

  // When the user clears the field, the map disappears. Retyping without
  // yet picking a fresh suggestion leaves the last pin in place — the
  // preview shows what will be saved, and the previous pick is still what
  // would save if the user changed their mind.
  useEffect(() => {
    if (value === '') setPreviewCoord(null);
  }, [value]);

  // Preview map: single stock pin at the picked coordinate. Created lazily
  // on the first coord so the slot is only mounted when it has something
  // to show; subsequent picks move the pin in place, no full rebuild.
  useEffect(() => {
    if (!mapkitOn || !previewCoord || !mapEl.current) return;

    if (!map.current) {
      map.current = new window.mapkit!.Map(mapEl.current, {
        // Preview inherits the surrounding theme; the readiness screen is
        // light, and the live view (dark) never opens this form.
        colorScheme: mapkit.Map.ColorSchemes.Light,
        showsCompass: mapkit.FeatureVisibility.Hidden,
        showsMapTypeControl: false,
        isRotationEnabled: false,
      });
    }
    const m = map.current;

    // Replace the pin rather than mutate — MarkerAnnotation's coordinate
    // isn't a live setter in every MapKit version, so a fresh annotation
    // is the portable answer and cheap at n=1.
    if (marker.current) m.removeAnnotations([marker.current]);
    const coord = new mapkit.Coordinate(previewCoord.latitude, previewCoord.longitude);
    // Accent blue, not alert red — a picked address on a preview is a
    // location, not a danger. Guardrail G12 reserves the alert red for
    // real alert surfaces, and the light-mode --accent (2a6fbe) is what
    // every non-alerting map pin already uses.
    marker.current = new mapkit.MarkerAnnotation(coord, { color: '#2a6fbe' });
    m.addAnnotations([marker.current]);

    // A tight span so the pin dominates — the preview's job is "is this
    // the right building", not "what's the neighbourhood like".
    m.setRegionAnimated(
      new mapkit.CoordinateRegion(coord, new mapkit.CoordinateSpan(0.003, 0.003)),
    );
  }, [previewCoord, mapkitOn]);

  // Destroy the map when it disappears from the DOM (coord cleared or
  // component unmounts) — a lingering MapKit instance keeps its token
  // refresh alive.
  useEffect(() => {
    if (previewCoord) return;
    map.current?.destroy();
    map.current = null;
    marker.current = null;
  }, [previewCoord]);

  useEffect(() => {
    return () => {
      map.current?.destroy();
      map.current = null;
      marker.current = null;
    };
  }, []);

  return (
    // The search hint in play, readable by a test (and by anyone inspecting
    // the page) — never a person's exact spot, it is the rounded region.
    <div className="relative" data-search-region={regionKey || undefined}>
      {field ? (
        /* The form register: the input wears the field and the label floats
           inside it — a real <label htmlFor>, so the name still reaches the
           input (and the tests). "Use my location" moves under the field. */
        <div className="relative">
          <input
            id={inputId}
            type="text"
            value={value}
            onChange={(e) => {
              setPickedWithoutCoord(false);
              setSearchFailed(false);
              picked.current = null;
              onChange(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            // Clicking back into a field that never lost focus fires no focus
            // event — reopen on the click too.
            onClick={() => setOpen(true)}
            onBlur={() => {
              // 150ms delay lets a mousedown on a suggestion fire before we
              // hide the list — otherwise the click is on the fading row and
              // React fires blur first.
              setTimeout(() => setOpen(false), 150);
            }}
            placeholder={placeholder ?? '100 Collins St, Melbourne'}
            autoComplete="off"
            spellCheck={false}
            className={`${FIELD} pr-3 focus:border-[var(--focus)] focus:ring-1 focus:ring-[var(--focus)]`}
          />
          {label ? (
            <label htmlFor={inputId} className={FIELD_LABEL}>
              {label}
            </label>
          ) : null}
        </div>
      ) : (
        <>
          {label ? (
            /* The shared Label, so this field's label matches every other field's
               (owner correction 2026-09-25: "Address" was the one sentence-case
               label on a form of uppercase ones). */
            <div className="mb-1.5 flex items-baseline justify-between gap-3">
              <label className="block" htmlFor={inputId}>
                <Label>{label}</Label>
              </label>
              {offerMyLocation && canAskLocation() ? (
                <button
                  type="button"
                  onClick={useMyLocation}
                  disabled={asking}
                  className="border-0 bg-transparent p-0 text-[13px] text-[var(--accent)] hover:underline disabled:opacity-60"
                >
                  {mine ? 'Searching near you' : asking ? 'Finding you…' : 'Use my location'}
                </button>
              ) : null}
            </div>
          ) : null}
          <input
            id={inputId}
            type="text"
            value={value}
            onChange={(e) => {
              setPickedWithoutCoord(false);
              setSearchFailed(false);
              picked.current = null;
              onChange(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            // Clicking back into a field that never lost focus fires no focus
            // event — reopen on the click too.
            onClick={() => setOpen(true)}
            onBlur={() => {
              // 150ms delay lets a mousedown on a suggestion fire before we
              // hide the list — otherwise the click is on the fading row and
              // React fires blur first.
              setTimeout(() => setOpen(false), 150);
            }}
            placeholder={placeholder ?? '100 Collins St, Melbourne'}
            autoComplete="off"
            spellCheck={false}
            className="h-9 w-full rounded-[var(--radius-1)] border border-[var(--line-2)] bg-[var(--paper)] px-3 text-[14px] text-[var(--ink)] outline-none focus:border-[var(--focus)]"
          />
        </>
      )}
      {field && offerMyLocation && canAskLocation() ? (
        <p className="mt-1 mb-0 px-0.5 text-[12px] leading-[16px]">
          <button
            type="button"
            onClick={useMyLocation}
            disabled={asking}
            className="border-0 bg-transparent p-0 text-[var(--accent)] hover:underline disabled:opacity-60"
          >
            {mine ? 'Searching near you' : asking ? 'Finding you…' : 'Use my location'}
          </button>
        </p>
      ) : null}
      {open && suggestions.length > 0 ? (
        <ul
          role="listbox"
          className={`absolute z-10 mt-1 max-h-[280px] w-full overflow-auto border border-[var(--line-2)] bg-[var(--paper)] shadow-lg ${field ? 'rounded-[10px]' : 'rounded-[var(--radius-1)]'}`}
        >
          {suggestions.map((s, i) => {
            const name = s.displayLines[0] ?? '';
            const rest = s.displayLines.slice(1).join(', ');
            return (
              <li key={`${name}-${i}`}>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    // Prevent focus-loss on the input before we get to
                    // click; without this the blur-close fires first
                    // and the click never reaches us.
                    e.preventDefault();
                  }}
                  onClick={() => pick(s)}
                  className="w-full text-left px-3 py-2 hover:bg-[var(--paper-3)]"
                >
                  <div className="text-[14px] text-[var(--ink)]">{name}</div>
                  {rest ? (
                    <div className="text-[12px] text-[var(--ink-2)] mt-0.5">{rest}</div>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
      {searchFailed ? (
        <p
          className={
            field
              ? 'mt-1 mb-0 px-0.5 text-[12px] leading-[16px] text-[var(--ink-2)]'
              : 'mt-1 text-[13px] text-[var(--ink-2)]'
          }
          role="status"
        >
          Address search isn&rsquo;t answering right now — keep typing, or tap the map to place the
          pin.
        </p>
      ) : null}
      {pickedWithoutCoord ? (
        <p
          className={
            field
              ? 'mt-1 mb-0 px-0.5 text-[12px] leading-[16px] text-[var(--ink-2)]'
              : 'mt-1 text-[13px] text-[var(--ink-2)]'
          }
          role="status"
        >
          That result has no location — tap the map to place the pin instead.
        </p>
      ) : null}
      {/* Preview map — one MapKit pin at the picked coordinate, so the
          person adding the building can see the pin land on the right
          spot before saving. Only mounted when MapKit is up AND a
          coordinate is in hand; when MapKit is off there is no fallback,
          just the field on its own, per the same degrade rule the
          autocomplete follows. */}
      {preview && mapkitOn && previewCoord ? (
        <div
          ref={mapEl}
          role="img"
          aria-label="Preview of the picked address on a map"
          className="mt-2 w-full overflow-hidden rounded-[var(--radius-1)] border border-[var(--line-2)]"
          style={{ height: 180 }}
        />
      ) : null}
    </div>
  );
}

function toMapkitRegion(r: SearchRegion): mapkit.CoordinateRegion {
  const mk = window.mapkit!;
  return new mk.CoordinateRegion(
    new mk.Coordinate(r.latitude, r.longitude),
    new mk.CoordinateSpan(r.latitudeDelta, r.longitudeDelta),
  );
}
